import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { isValidLeadWebsiteUrl } from '../lib/leadFormWebsiteUrl.js'
import { fieldLabel, t } from '../lib/widgetLocale.js'

const FormContext = createContext(null)

const FIELD_LABELS = {
  firstName: 'First Name',
  lastName: 'Last Name',
  name: 'Name',
  email: 'Email',
  phone: 'Phone',
  website: 'Website',
  company: 'Company',
  comments: 'Comments',
}

const TEMPLATES = {
  email: ['email'],
  nameEmail: ['name', 'email'],
  nameEmailPhone: ['name', 'email', 'phone'],
  nameEmailPhoneWebsite: ['name', 'email', 'phone', 'website'],
  nameEmailPhoneCompanyWebsite: [
    'name',
    'email',
    'phone',
    'company',
    'website',
  ],
  firstLastEmail: ['firstName', 'lastName', 'email'],
  firstLastEmailPhone: ['firstName', 'lastName', 'email', 'phone'],
  firstLastEmailPhoneWebsite: [
    'firstName',
    'lastName',
    'email',
    'phone',
    'website',
  ],
  firstLastEmailPhoneCompanyWebsite: [
    'firstName',
    'lastName',
    'email',
    'phone',
    'company',
    'website',
  ],
  nameEmailComments: ['name', 'email', 'comments'],
  nameEmailPhoneComments: ['name', 'email', 'phone', 'comments'],
}

function inferInputType(name) {
  switch (name) {
    case 'email':
      return 'email'
    case 'phone':
      return 'tel'
    case 'website':
      return 'url'
    case 'comments':
      return 'textarea'
    default:
      return 'text'
  }
}

function humanizeFieldLabel(name) {
  switch (name) {
    case 'firstName':
      return 'First name'
    case 'lastName':
      return 'Last name'
    default:
      return name.charAt(0).toUpperCase() + name.slice(1)
  }
}

function presetFieldDefinitions(template, lang) {
  const keys = TEMPLATES[template] || TEMPLATES.email
  return keys.map((name, i) => ({
    id: `preset_${name}_${i}`,
    name,
    label: fieldLabel(name, lang),
    inputType: inferInputType(name),
    required: true,
    options: [],
    allowMultiple: false,
  }))
}

/**
 * Ordered field definitions for rendering and validation (builder schema
 * or template presets).
 */
export function getLeadFormFieldDefinitions(form, lang) {
  if (!form) return []
  if (Array.isArray(form.steps) && form.steps.length > 0) {
    const out = []
    for (const step of form.steps) {
      for (const f of step.fields || []) {
        if (!f?.name) continue
        out.push({
          id: f.id || f.name,
          name: f.name,
          label: f.label || fieldLabel(f.name, lang),
          inputType: f.inputType || inferInputType(f.name),
          required: Boolean(f.required),
          options: Array.isArray(f.options) ? f.options : [],
          allowMultiple: Boolean(f.allowMultiple),
        })
      }
    }
    return out
  }

  const cfg = form.config || {}
  const fields = cfg.fields
  if (Array.isArray(fields) && fields.length > 0) {
    return fields.map((f, i) => ({
      id: f.id || `field_${f.name}_${i}`,
      name: f.name,
      label: f.label || fieldLabel(f.name, lang),
      inputType: f.inputType || inferInputType(f.name),
      required: Boolean(f.required),
      options: Array.isArray(f.options) ? f.options : [],
      allowMultiple: Boolean(f.allowMultiple),
    }))
  }

  return presetFieldDefinitions(form.template || 'email', lang)
}

/** @deprecated use getLeadFormFieldDefinitions + filter required */
export function getRequiredFieldsFromForm(form) {
  return getLeadFormFieldDefinitions(form)
    .filter((f) => f.required)
    .map((f) => f.name)
}

export function validateFormFields(formData, definitions, lang) {
  const errors = {}
  for (const field of definitions) {
    if (!field.required) continue
    const key = field.name
    const value = formData[key]
    const inputType = field.inputType || 'text'

    if (inputType === 'checkbox' && field.allowMultiple) {
      if (!Array.isArray(value) || value.length === 0) {
        errors[key] = t('form.required', lang, { label: field.label })
      }
      continue
    }
    if (inputType === 'checkbox' && !field.allowMultiple) {
      if (!value) {
        errors[key] = t('form.required', lang, { label: field.label })
      }
      continue
    }
    const trimmed = value == null ? '' : String(value).trim()
    if (trimmed === '') {
      errors[key] = t('form.required', lang, { label: field.label })
      continue
    }
    if (inputType === 'url' && !isValidLeadWebsiteUrl(trimmed)) {
      errors[key] = t('form.invalidUrl', lang)
    }
  }
  return { errors, isValid: Object.keys(errors).length === 0 }
}

export function validateRequiredFields(formData, requiredKeys, lang) {
  const errors = {}
  for (const key of requiredKeys) {
    const value = formData[key]
    if (value == null || String(value).trim() === '') {
      errors[key] = t('form.required', lang, {
        label: fieldLabel(key, lang),
      })
    }
  }
  return { errors, isValid: Object.keys(errors).length === 0 }
}

/**
 * Lightweight form context. The legacy version used `useImmerReducer` -
 * we don't need that complexity here because the widget's form surface
 * is at most one active form at a time. Plain `useState` keeps the
 * bundle smaller and keeps form state from leaking across instances.
 */
export function FormProvider({ children }) {
  const [activeForm, setActiveFormState] = useState(null)
  const [isFormOpen, setFormOpen] = useState(false)
  const [formColumn, setFormColumn] = useState('left')
  const [formData, setFormData] = useState({})
  const [formErrors, setFormErrors] = useState({})
  const [isSubmitting, setSubmitting] = useState(false)
  const [isSubmitted, setSubmitted] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)

  const openForm = useCallback((form, { column = 'left' } = {}) => {
    setActiveFormState(form)
    setFormColumn(column === 'right' ? 'right' : 'left')
    setFormOpen(true)
    setFormData({})
    setFormErrors({})
    setSubmitted(false)
    setCurrentStep(0)
  }, [])

  const closeForm = useCallback(() => {
    setFormOpen(false)
    setActiveFormState(null)
    setFormColumn('left')
    setFormData({})
    setFormErrors({})
    setCurrentStep(0)
  }, [])

  const updateFormData = useCallback((data) => {
    setFormData((prev) => ({ ...prev, ...data }))
  }, [])

  const handleFormSubmit = useCallback(
    (data, form, lang) => {
      const target = form || activeForm
      const defs = getLeadFormFieldDefinitions(target, lang)
      const { errors, isValid } = validateFormFields(data, defs, lang)
      if (!isValid) {
        setFormErrors(errors)
        return { success: false, errors }
      }
      setFormErrors({})
      setSubmitting(true)
      return { success: true }
    },
    [activeForm],
  )

  const value = useMemo(
    () => ({
      activeForm,
      isFormOpen,
      formColumn,
      formData,
      formErrors,
      isSubmitting,
      isSubmitted,
      currentStep,
      openForm,
      closeForm,
      updateFormData,
      setFormErrors,
      setSubmitting,
      setSubmitted,
      setCurrentStep,
      handleFormSubmit,
    }),
    [
      activeForm,
      isFormOpen,
      formColumn,
      formData,
      formErrors,
      isSubmitting,
      isSubmitted,
      currentStep,
      openForm,
      closeForm,
      updateFormData,
      handleFormSubmit,
    ],
  )

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>
}

export function useForm() {
  const ctx = useContext(FormContext)
  if (!ctx) throw new Error('useForm must be used inside <FormProvider>')
  return ctx
}
